/**
 * Activity's All activity — `docs/activity.md` §8.0, §8.3 (Board 10).
 *
 * The list engine, unchanged and embedded, over area `activity-feed`: every
 * text, email, call, visit, meeting, task and note for the people the people
 * button picks — newest first, grouped by day, the day's five figures on top
 * (the engine's fixed `metrics`, not stat cards). The pick travels as the one
 * base condition `whose is any of …`; the area's `from` enforces who may be
 * seen (rule 3).
 *
 * A row's tray is the screen's (the area has none on the server): a voice
 * note's transcript, its recording and the photos taken with it; a message's
 * whole text; a call's outcome. A click opens the conversation, the Task
 * sheet, a visit's or meeting's card — or, for a call or a note, which have
 * no sheet of their own, the record it is about.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  KIND_STYLES, addDays, zonedLocal, type CalendarItem, type FilterCondition, type KindLook,
} from '@rowted/shared'
import { ListScreen, type ListMetric } from '../../components/list-screen.js'
import { useList, type ListRow, type UseListResult } from '../../lib/use-list.js'
import { formatNumber } from '../../lib/format.js'
import { usePhone } from '../../lib/use-narrow.js'
import { cn } from '../../lib/cn.js'
import { activityRequest, peopleQuery } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { Face, FaceStack, type FacePerson } from '../faces.js'
import { LinkChips, rowFaces, usePeopleById } from '../tasks/parts.js'
import { openTaskSheet, TaskSheetHost, TASKS_CHANGED_EVENT } from '../tasks/task-sheet.js'
import { useActivityNav } from '../use-activity-history.js'
import { useActivityHostBar } from '../page-views.js'
import type { Figure, TaskLink } from '../tasks/types.js'
import { clockWords, dayLabel } from '../tasks/words.js'
import { StopCard } from '../calendar/overlays.js'

const ALWAYS = [
  'when', 'kind', 'what', 'preview', 'links', 'peopleIds', 'whoName', 'automatic', 'notAnswered', 'onSite',
  'outcome', 'conversationId', 'source', 'day', 'faces',
]

const TODAY = 'Today'

/** A feed row's kind as the kinds' look (§3): a text in or out is a text. */
const LOOK: Record<string, KindLook> = {
  sms_in: 'sms', sms_out: 'sms', email_in: 'email', email_out: 'email', call: 'call', note: 'note',
  voice: 'voice_note', task: 'task', appointment: 'site_visit', meeting: 'meeting',
}

const KIND_WORDS: Record<string, string> = {
  sms_in: 'Text in', sms_out: 'Text out', email_in: 'Email in', email_out: 'Email out', call: 'Call', note: 'Note',
  voice: 'Voice note', task: 'Task', appointment: 'Site visit', meeting: 'Meeting',
}

const FIGURE_DOTS: Record<Figure['key'], string> = {
  texts: KIND_STYLES.sms.fg,
  emails: KIND_STYLES.email.fg,
  calls: KIND_STYLES.call.fg,
  visits: KIND_STYLES.site_visit.fg,
  tasksLate: 'rgb(var(--color-danger))',
}

const str = (value: unknown): string => (value === null || value === undefined ? '' : String(value))

/**
 * A site visit's or meeting's row as the stop card's item: its day and start
 * from *when*, its end from the row's own *8:30–9:45* (the card's answer then
 * says the rest).
 */
function stopItemOf(row: ListRow, timeZone: string): CalendarItem {
  const local = zonedLocal(new Date(String(row.when)), timeZone)
  const until = /–(\d{1,2}):(\d{2})/.exec(String(row.preview ?? ''))
  let end = until ? (Number(until[1]) % 12) * 60 + Number(until[2]) : local.minutes + 60
  while (end <= local.minutes) end += 12 * 60
  const job = (Array.isArray(row.links) ? row.links as TaskLink[] : []).find((link) => link.type === 'job')
  return {
    id: String(row.id), key: String(row.id), kind: row.kind === 'meeting' ? 'meeting' : 'appointment',
    occurrence: null, startsAt: String(row.when), endsAt: String(row.when), date: local.date,
    start: local.minutes, end, title: String(row.what ?? ''), sub: null, place: null, remote: false, tags: [],
    editable: false, people: [], done: false, blockType: null, repeatRule: null, allDay: false,
    jobId: job?.id ?? null, jobReference: job?.label ?? null,
  }
}
const linksOf = (row: ListRow): TaskLink[] => (Array.isArray(row.links) ? row.links as TaskLink[] : [])
const idsOf = (row: ListRow): string[] => (Array.isArray(row.peopleIds) ? row.peopleIds as string[] : [])

/** The 28 px kind tile (§8.3): the kind's colour on its tint. */
export function KindTile({ kind, size = 28 }: { kind: string; size?: number }) {
  const look = KIND_STYLES[LOOK[kind] ?? 'note']
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-lg"
      style={{ width: size, height: size, background: look.bg, color: look.fg }}>
      <Glyph name={look.icon as GlyphName} size={Math.round(size * 0.54)} />
    </span>
  )
}

export function AllTab() {
  const { me, today, people, unassigned, state, change } = useActivity()
  const phone = usePhone()
  const navigate = useNavigate()
  const nav = useActivityNav()
  const timeZone = me.user.timeZone
  const baseFilter: FilterCondition[] = useMemo(
    () => [{ field: 'whose', op: 'in', value: [...people, ...unassigned] }], [people, unassigned])
  const list = useList('activity-feed', { alwaysFetch: ALWAYS, baseFilter })
  /* On a phone the views are tabs under *Activity* and the count the line under it, as on Tasks (Aaron, Oct 27). */
  const hostBar = useActivityHostBar(phone)
  const byId = usePeopleById()

  /* The figures (§8.3), for the same people. */
  const [figures, setFigures] = useState<Figure[]>([])
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const again = () => setTick((n) => n + 1)
    window.addEventListener(TASKS_CHANGED_EVENT, again)
    return () => window.removeEventListener(TASKS_CHANGED_EVENT, again)
  }, [])
  useEffect(() => {
    let live = true
    activityRequest<{ items: Figure[] }>(`/activity/figures?people=${peopleQuery(people)}&unassigned=${peopleQuery(unassigned)}`)
      .then((answer) => { if (live) setFigures(answer.items) })
      .catch(() => { if (live) setFigures([]) })
    return () => { live = false }
  }, [people, unassigned, tick])
  const metrics: ListMetric[] = figures.map((figure) => ({
    key: figure.key,
    label: figure.label,
    value: formatNumber(figure.value),
    sub: figure.sub,
    subUrgent: figure.urgent,
    dotColor: FIGURE_DOTS[figure.key],
    filter: figure.filter.conditions as FilterCondition[],
  }))

  /* Open on the remembered view, else *Today* (§8.3) — once, and never over a link that names one. */
  const landed = useRef(false)
  useEffect(() => {
    if (landed.current || list.savedViews.length === 0) return
    landed.current = true
    if (list.activeViewId) return
    const remembered = state.all.viewId ? list.savedViews.find((view) => view.id === state.all.viewId) : null
    const target = remembered ?? list.savedViews.find((view) => view.name === TODAY)
    if (target) list.openSavedView(target.id)
  }, [list, state.all.viewId])
  useEffect(() => {
    if (!list.activeViewId || list.activeViewId === state.all.viewId) return
    change({ all: { viewId: list.activeViewId } })
  }, [list.activeViewId, state.all.viewId, change])

  const whenWords = (row: ListRow): string => {
    const at = str(row.when)
    if (!at) return ''
    const local = zonedLocal(new Date(at), timeZone)
    const time = clockWords(local.minutes)
    return list.view.group === 'day' ? time : `${dayLabel(local.date)} · ${time}`
  }

  const groupLabel = (key: string): string => {
    if (list.view.group !== 'day' || !/^\d{4}-\d{2}-\d{2}/.test(key)) return key === '—' ? 'No value' : key
    const day = key.slice(0, 10)
    if (day === today) return `Today · ${dayLabel(day)}`
    if (day === addDays(today, -1)) return `Yesterday · ${dayLabel(day)}`
    return dayLabel(day)
  }

  const whoCell = (row: ListRow) => {
    const own = rowFaces(row.faces)
    const faces = own.length > 0 ? own.map((face) => byId.get(face.id) ?? face)
      : idsOf(row).map((id) => byId.get(id)).filter((p): p is FacePerson => Boolean(p))
    const note = row.onSite === true ? 'on site' : row.notAnswered === true ? 'not answered'
      : row.automatic === true ? 'sent by Rowted' : null
    if (faces.length > 1) {
      return (
        <span className="flex min-w-0 items-center gap-[7px]">
          <FaceStack people={faces} size={20} max={3} />
          <span className="truncate text-[13px]">{faces.map((p) => p.name.split(' ')[0]).join(', ')}</span>
        </span>
      )
    }
    const person = faces[0]
    const name = person?.name ?? str(row.whoName)
    if (!name) return <span className="text-[13px] text-text-faint">Unassigned</span>
    return (
      <span className="flex min-w-0 items-center gap-[7px]">
        {person && <Face person={person} size={22} />}
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[13px]">{name}</span>
          {note && <span className="mt-px truncate text-[11.5px] text-text-muted">{note}</span>}
        </span>
      </span>
    )
  }

  const kindCell = (row: ListRow) => {
    const kind = str(row.kind)
    const look = KIND_STYLES[LOOK[kind] ?? 'note']
    return (
      <span className="flex items-center gap-1.5 whitespace-nowrap">
        <span className="text-[12.5px] font-semibold" style={{ color: look.fg }}>{KIND_WORDS[kind] ?? kind}</span>
        {row.automatic === true && <span className="text-[11.5px] text-text-faint">auto</span>}
      </span>
    )
  }

  /* A site visit or meeting opens its card beside the row (§8.3: the activity's sheet). */
  const [stop, setStop] = useState<{ item: CalendarItem; anchor: HTMLElement } | null>(null)
  const open = (row: ListRow) => {
    const kind = str(row.kind)
    if (str(row.source) === 'message' && str(row.conversationId)) {
      /* Over All activity: back from the conversation comes back to this list (Aaron, Oct 26). */
      nav.push({ tab: 'inbox', c: str(row.conversationId) }, { fresh: true })
      return
    }
    if (kind === 'task') { openTaskSheet(row.id); return }
    if (kind === 'appointment' || kind === 'meeting') {
      /* Beside the row's *What*, so the card stands next to what was clicked rather than over the list's start. */
      const line = document.querySelector<HTMLElement>(`[data-row="${CSS.escape(String(row.id))}"]`)
      const anchor = line?.querySelector<HTMLElement>('[data-fit="what"]') ?? line
      if (anchor) { setStop({ item: stopItemOf(row, timeZone), anchor }); return }
    }
    const link = linksOf(row)[0]
    if (link) navigate(link.type === 'job' ? `/jobs/${link.id}` : link.type === 'property' ? `/properties/${link.id}` : `/contacts/${link.id}`)
  }

  /* Messages from customers read in ink; everything else's line is muted (Board 10). */
  const inbound = (row: ListRow) => (str(row.kind) === 'sms_in' || str(row.kind) === 'email_in') && row.automatic !== true

  const shown: UseListResult = list

  return (
    <div className={cn('flex min-w-0 flex-1 flex-col', phone ? 'px-3.5 pt-2.5' : 'p-4')}>
      <ListScreen
        area="activity-feed"
        title="All activity"
        chrome="embedded"
        hostBar={hostBar}
        cards={false}
        list={shown}
        metrics={metrics}
        primaryColumn={{
          field: 'what',
          header: 'What',
          render: (row) => ({
            primary: str(row.what),
            secondary: str(row.preview)
              ? <span className={inbound(row) ? 'text-text' : undefined}>{str(row.preview)}</span>
              : undefined,
          }),
        }}
        mark={{ width: 44, render: (row) => <KindTile kind={str(row.kind)} /> }}
        cardLine={(row) => ({ trailing: <span className="text-[12px] text-text-muted">{whenWords(row)}</span> })}
        cell={{
          when: (row) => <span className="whitespace-nowrap text-[13px]">{whenWords(row)}</span>,
          kind: kindCell,
          attached: (row) => (linksOf(row).length ? <LinkChips links={linksOf(row)} /> : null),
          who: whoCell,
          whoName: whoCell,
        }}
        columnWidths={{ when: 96, kind: 120, attached: 270, who: 150, whoName: 150 }}
        groupLabel={(key) => groupLabel(key)}
        tray={(row) => <FeedTray row={row} />}
        rowMenu={(row) => [{ key: 'open', label: 'Open', onSelect: () => open(row) }]}
        onRowClick={open}
        emptyTitle="Nothing yet"
        emptyBody="Texts, emails, calls, visits, meetings, tasks and notes for the people you have picked show here."
        importOrgUnitId={me.scope.selected[0]}
      />
      <TaskSheetHost />
      {stop && (
        <StopCard item={stop.item} anchor={stop.anchor} onClose={() => setStop(null)} onChanged={() => list.reload()} />
      )}
    </div>
  )
}

interface FeedItem {
  id: string
  source: 'activity' | 'message'
  kind: string
  body: string
  subject: string | null
  outcome: string | null
  durationMinutes: number | null
  files: { id: string; type: string; contentType: string; name: string; durationSeconds: number | null }[]
}

const seconds = (n: number): string => `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`

function filesWords(files: FeedItem['files']): string {
  const photos = files.filter((f) => f.type === 'photo').length
  const videos = files.filter((f) => f.type === 'video').length
  const parts: string[] = []
  if (photos) parts.push(photos === 1 ? '1 photo' : `${photos} photos`)
  if (videos) parts.push(videos === 1 ? '1 video' : `${videos} videos`)
  return parts.join(' · ')
}

/** §8.3's tray: what the row has inside it. */
function FeedTray({ row }: { row: ListRow }) {
  const navigate = useNavigate()
  const [item, setItem] = useState<FeedItem | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const audio = useRef<HTMLAudioElement>(null)
  useEffect(() => {
    let live = true
    activityRequest<FeedItem>(`/activity/feed-items/${row.id}`)
      .then((value) => { if (live) setItem(value) })
      .catch((cause: unknown) => { if (live) setError(cause instanceof Error ? cause.message : 'This could not open.') })
    return () => { live = false }
  }, [row.id])
  if (error) return <div className="text-[12.5px] text-danger">{error}</div>
  if (!item) return <div className="text-[12.5px] text-text-muted">Loading…</div>

  const recording = item.files.find((f) => f.type === 'audio')
  const pictures = item.files.filter((f) => f.type === 'photo' || f.type === 'video')
  const job = linksOf(row).find((link) => link.type === 'job')
  const kind = str(row.kind)
  const card = 'min-w-0 rounded-[10px] border border-line bg-surface px-3 py-2.5'
  const button = 'inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-line bg-surface px-3.5 text-[12.5px] font-semibold hover:bg-hover'

  const words = kind === 'voice' ? 'Transcript'
    : item.source === 'message' ? (kind.startsWith('email') ? 'Email' : 'Text')
      : kind === 'call' ? 'Call' : 'Note'
  const text = item.source === 'message' && item.subject ? `${item.subject}\n\n${item.body}` : item.body

  return (
    <div className="flex gap-3">
      <div className={cn(card, 'flex-1')}>
        <div className="flex items-center gap-1.5">
          <span className="text-text-muted"><Glyph name={kind === 'voice' ? 'mic' : kind === 'call' ? 'phone' : item.source === 'message' ? 'bubble' : 'pencil'} size={13} /></span>
          <b className="text-[12px]">{words}</b>
          <span className="flex-1" />
          {recording?.durationSeconds != null && (
            <span className="inline-flex items-center gap-1 text-[11.5px] text-text-muted">
              <Glyph name="play" size={11} />{seconds(recording.durationSeconds)}
            </span>
          )}
          {kind === 'call' && item.durationMinutes != null && (
            <span className="text-[11.5px] text-text-muted">{item.durationMinutes} min</span>
          )}
        </div>
        {kind === 'call' && item.outcome && <div className="mt-1.5 text-[12.5px] font-semibold text-text-body">{item.outcome}</div>}
        <div className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-[1.5] text-text-body">{text}</div>
      </div>
      {pictures.length > 0 && (
        <div className={cn(card, 'w-[300px] shrink-0')}>
          <div className="flex items-center gap-1.5">
            <span className="text-text-muted"><Glyph name="camera" size={13} /></span>
            <b className="text-[12px]">{filesWords(pictures)}</b>
          </div>
          <div className="mt-2 flex gap-1.5">
            {pictures.slice(0, 4).map((file) => (file.type === 'photo' ? (
              <img key={file.id} src={`/api/v1/files/${file.id}`} alt={file.name}
                className="h-[46px] w-[62px] shrink-0 rounded-[7px] bg-line-soft object-cover" />
            ) : (
              <span key={file.id} className="relative inline-flex h-[46px] w-[62px] shrink-0 items-center justify-center overflow-hidden rounded-[7px] bg-line-strong text-white">
                <Glyph name="play" size={14} />
              </span>
            )))}
          </div>
        </div>
      )}
      {(job || recording) && (
        <div className="flex shrink-0 flex-col gap-2">
          {job && (
            <button type="button" className={button} onClick={() => navigate(`/jobs/${job.id}`)}>
              <Glyph name="layers" size={14} />Open {job.label}
            </button>
          )}
          {recording && (
            <>
              <button type="button" className={button} onClick={() => {
                const node = audio.current
                if (!node) return
                if (node.paused) { void node.play(); setPlaying(true) } else { node.pause(); setPlaying(false) }
              }}>
                <Glyph name={playing ? 'pause' : 'play'} size={14} />{playing ? 'Pause' : 'Play'}
              </button>
              <audio ref={audio} src={`/api/v1/files/${recording.id}`} preload="none" onEnded={() => setPlaying(false)} />
            </>
          )}
        </div>
      )}
    </div>
  )
}
